
import type { PropType } from 'vue'
import type {
  DataTableColumn,
  IndexJobDeploymentDTO,
  IndexJobDTO,
  IndexMessages,
} from '../../models'
import { computed, defineComponent, getCurrentInstance, ref } from 'vue'
import { useIndexJobPivots } from '../../composables/index/use-index-job-pivots'
import { AlignEnum } from '../../enums'
import { capitalizeSentence } from '../../filters/capitalize-sentence.filter'
import DataTable from '../crud/data-table.component.vue'
import { UiModal } from '../keen-ui'
import IndexJobStateBadge from './index-job-state-badge.component.vue'

export default defineComponent({
  name: 'IndexJobDetailsModal',
  components: {
    DataTable,
    IndexJobStateBadge,
    UiModal,
  },
  props: {
    job: {
      type: Object as PropType<IndexJobDTO>,
      default: undefined,
    },
  },
  setup(props) {
    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const messages = ctx.proxy.$epb.messages.tools.index as IndexMessages

    const { getPivots, getPivotLabel, getPivotValueLabel } = useIndexJobPivots()

    const baseClass = 'epb-index-job-details-modal'

    const modal = ref()

    const title = computed<string>(() =>
      ctx.proxy.$epb.utils.compileMessage(messages.details.title, { id: props.job.id }),
    )

    const pivotKeys = computed<Array<string>>(() => Object.keys(getPivots(props.job)))

    const columns = computed<Array<DataTableColumn>>(() => [
      {
        id: 'id',
        text: messages.fields.deploymentId.label,
        width: '75%',
      },
      ...pivotKeys.value.map(pivotKey => ({
        id: pivotKey,
        text: capitalizeSentence(getPivotLabel(props.job, pivotKey)),
      })),
      {
        id: 'created',
        text: messages.fields.created.label,
      },
      {
        id: 'state',
        text: messages.fields.state.label,
        width: '3.5rem',
        align: AlignEnum.CENTER,
      },
    ])

    function open(): void {
      modal.value?.open()
    }

    function getStateTooltip(deployment: IndexJobDeploymentDTO): string {
      let result = capitalizeSentence(messages.fields.state.values[deployment.state])
      if (deployment.state === 'FAILURE' && deployment.errors.length) {
        result += `<br>${deployment.errors.join('<br>')}`
      }
      return result
    }

    return {
      baseClass,
      modal,
      title,
      pivotKeys,
      columns,
      open,
      getPivotValueLabel,
      capitalizeSentence,
      getStateTooltip,
    }
  },
})
